import React, { useState, useEffect, useRef } from "react";
import {
  createPropertyAsset,
  updatePropertyAsset,
  fetchPropertyCategoryDropdown,
  fetchPropertyConditionDropdown,
  fetchPropertyStatusDropdown,
  fetchPropertyAssetById,
} from "../api/propertyAssetsApi";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import ResponsiblePersonSearch from "./ResponsiblePersonSearch";
import { masterSelectOptions } from "../../memberships/utils/masterLookupUtils";
import { validatePropertyAssetForm } from "../validations/propertyAssetValidation";
import {
  buildPropertyAssetImageUrl,
  getPropertyAssetImageFileName,
} from "../utils/propertyAssetHelpers";
import { compressPhoto } from "../../../utils/compressImage";

const EMPTY_FORM = {
  asset_code: "",
  property_asset_name: "",
  category_id: "",
  quantity: "",
  brand_model: "",
  purchase_date: "",
  purchase_value: "",
  supplier_vendor: "",
  invoice_bill_no: "",
  location_room: "",
  condition_id: "",
  property_status_id: "",
  responsible_person_id: "",
  warranty_expiry_date: "",
  physical_verification_date: "",
  remarks: "",
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];

const formatDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const PropertyAssetForm = ({
  mode = "full",
  onSuccess,
  lookupsRefreshKey = 0,
  editingRecord,
  onCancelEdit,
}) => {
  const isSubAdmin = mode === "subAdmin";
  const isEditing = !!editingRecord?.id;
  const isSubAdminLimitedEdit = isSubAdmin && isEditing;
  const showFullAssetForm = !isSubAdmin || !isEditing;

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [imageFile, setImageFile] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState("");
  const [responsibleLabel, setResponsibleLabel] = useState("");
  const [lookups, setLookups] = useState({
    categories: [],
    conditions: [],
    statuses: [],
  });
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);

  const alertRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        if (isSubAdminLimitedEdit) {
          const condRes = await fetchPropertyConditionDropdown();
          setLookups((prev) => ({
            ...prev,
            conditions: condRes?.data || [],
          }));
          return;
        }
        const [catRes, condRes, statRes] = await Promise.all([
          fetchPropertyCategoryDropdown(),
          fetchPropertyConditionDropdown(),
          fetchPropertyStatusDropdown(),
        ]);
        setLookups({
          categories: catRes?.data || [],
          conditions: condRes?.data || [],
          statuses: statRes?.data || [],
        });
      } catch (err) {
        console.error("Failed to load property asset lookups", err);
      }
    };
    loadLookups();
  }, [lookupsRefreshKey, isSubAdminLimitedEdit]);

  const handleReset = () => {
    setFormData({ ...EMPTY_FORM });
    setImageFile(null);
    setExistingImagePath("");
    setResponsibleLabel("");
    setError(null);
    setErrorDetails({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    const loadEditing = async () => {
      if (!editingRecord?.id) {
        handleReset();
        return;
      }

      setLoadingRecord(true);
      try {
        const resp = await fetchPropertyAssetById(editingRecord.id);
        const row = resp?.data ?? resp;
        if (!row) return;

        setFormData({
          ...EMPTY_FORM,
          asset_code: row.asset_code || "",
          property_asset_name: row.property_asset_name || "",
          category_id: row.category_id != null ? String(row.category_id) : "",
          quantity: row.quantity != null ? String(row.quantity) : "",
          brand_model: row.brand_model || "",
          purchase_date: formatDateForInput(row.purchase_date),
          purchase_value: row.purchase_value != null ? String(row.purchase_value) : "",
          supplier_vendor: row.supplier_vendor || "",
          invoice_bill_no: row.invoice_bill_no || "",
          location_room: row.location_room || "",
          condition_id: row.condition_id != null ? String(row.condition_id) : "",
          property_status_id:
            row.property_status_id != null ? String(row.property_status_id) : "",
          responsible_person_id:
            row.responsible_person_id != null ? String(row.responsible_person_id) : "",
          warranty_expiry_date: formatDateForInput(row.warranty_expiry_date),
          physical_verification_date: formatDateForInput(row.physical_verification_date),
          remarks: row.remarks || "",
        });
        setResponsibleLabel(
          row.responsible_person_name ||
            editingRecord?.responsible_person_name ||
            "",
        );
        setExistingImagePath(row.asset_image_url || "");
        setImageFile(null);
        setError(null);
        setErrorDetails({});
      } catch (err) {
        setError(err.message || "Failed to load asset details");
      } finally {
        setLoadingRecord(false);
      }
    };

    loadEditing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRecord?.id]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorDetails[name]) {
      setErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageFile(null);
      e.target.value = "";
      setErrorDetails((prev) => ({
        ...prev,
        asset_image_url: "Upload a JPG or PNG image",
      }));
      return;
    }
    try {
      const compressed = await compressPhoto(file);
      setImageFile(compressed);
      setExistingImagePath("");
      if (errorDetails.asset_image_url) {
        setErrorDetails((prev) => ({ ...prev, asset_image_url: "" }));
      }
    } catch {
      setImageFile(null);
      e.target.value = "";
      setErrorDetails((prev) => ({
        ...prev,
        asset_image_url: "Could not compress image",
      }));
    }
  };

  const buildFormDataPayload = () => {
    const fd = new FormData();
    const appendIf = (key, val) => {
      if (val != null && val !== "") fd.append(key, String(val));
    };

    if (isSubAdminLimitedEdit) {
      appendIf("property_asset_name", formData.property_asset_name.trim());
      appendIf("location_room", formData.location_room.trim());
      appendIf("condition_id", formData.condition_id);
      appendIf("brand_model", formData.brand_model.trim());
      appendIf("remarks", formData.remarks.trim());
      return fd;
    }

    appendIf("property_asset_name", formData.property_asset_name.trim());
    appendIf("category_id", formData.category_id);
    appendIf("quantity", formData.quantity);
    appendIf("brand_model", formData.brand_model.trim());
    appendIf("purchase_date", formData.purchase_date);
    appendIf("purchase_value", formData.purchase_value);
    appendIf("supplier_vendor", formData.supplier_vendor.trim());
    appendIf("invoice_bill_no", formData.invoice_bill_no.trim());
    appendIf("location_room", formData.location_room.trim());
    appendIf("condition_id", formData.condition_id);
    appendIf("property_status_id", formData.property_status_id);
    appendIf("responsible_person_id", formData.responsible_person_id);
    appendIf("warranty_expiry_date", formData.warranty_expiry_date);
    appendIf("physical_verification_date", formData.physical_verification_date);
    appendIf("remarks", formData.remarks.trim());

    if (imageFile) {
      fd.append("asset_image_url", imageFile);
    }

    return fd;
  };

  const handleSubmit = async () => {
    setError(null);
    setErrorDetails({});

    const validationPayload = {
      ...formData,
      _hasNewImage: !!imageFile || !!existingImagePath,
    };
    const localErrors = validatePropertyAssetForm(validationPayload, {
      isEdit: isEditing,
      requireImage: true,
      mode: isSubAdminLimitedEdit ? "subAdmin" : "full",
    });
    if (Object.keys(localErrors).length > 0) {
      setErrorDetails(localErrors);
      setError("Please fix the highlighted fields.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const fd = buildFormDataPayload();
      const result = isEditing
        ? await updatePropertyAsset(editingRecord.id, fd, csrfToken)
        : await createPropertyAsset(fd, csrfToken);

      if (result?.error) {
        setError(result.error.message || "Failed to save asset");
        setErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }

      onSuccess?.(
        isEditing
          ? "Property asset updated successfully."
          : "Property asset created successfully.",
      );
      if (isEditing) {
        onCancelEdit?.();
      } else {
        handleReset();
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const ALERT_ID = "property-asset-form-alert";
  const today = new Date().toISOString().split("T")[0];

  const categoryOptions = masterSelectOptions(lookups.categories, {
    labelKey: "category_name",
    selectedValue: formData.category_id,
  });
  const conditionOptions = masterSelectOptions(lookups.conditions, {
    labelKey: "condition_name",
    selectedValue: formData.condition_id,
  });
  const statusOptions = masterSelectOptions(lookups.statuses, {
    labelKey: "status_name",
    selectedValue: formData.property_status_id,
  });

  if (loadingRecord) {
    return <p className="ub-loading">Loading asset details…</p>;
  }

  return (
    <GlobalFormShell
      legend={
        isEditing
          ? isSubAdminLimitedEdit
            ? "Edit asset (limited)"
            : "Edit property / asset"
          : "Add property / asset"
      }
      showAlert={!!error}
      alertText={error}
      alertId={ALERT_ID}
      formAlertRef={alertRef}
      actions={
        <>
          <GlobalButton
            type="button"
            variant="primary"
            disabled={loading}
            onClick={handleSubmit}
            loading={loading}
          >
            {isEditing ? "Update asset" : "Save asset"}
          </GlobalButton>
          {isEditing ? (
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => onCancelEdit?.()}
              disabled={loading}
            >
              Cancel
            </GlobalButton>
          ) : (
            <GlobalButton type="button" variant="ghost" onClick={handleReset} disabled={loading}>
              Reset
            </GlobalButton>
          )}
        </>
      }
    >
      {!isSubAdmin && isEditing ? (
        <GlobalTextField
          id="asset_code"
          label="Asset code"
          value={formData.asset_code || "Auto-generated on save"}
          onChange={() => {}}
          disabled
        />
      ) : null}

      <GlobalTextField
        id="property_asset_name"
        label="Property / asset name"
        value={formData.property_asset_name}
        onChange={(v) => setFormField("property_asset_name", v)}
        error={errorDetails.property_asset_name}
        baseAlertId={ALERT_ID}
        required
      />

      {showFullAssetForm ? (
        <>
          <GlobalSelectField
            id="category_id"
            label="Category"
            value={formData.category_id}
            onChange={(v) => setFormField("category_id", v)}
            options={categoryOptions}
            error={errorDetails.category_id}
            baseAlertId={ALERT_ID}
            required
            placeholder="Select category"
          />
          <GlobalTextField
            id="quantity"
            label="Quantity"
            type="text"
            inputMode="numeric"
            value={formData.quantity}
            onChange={(v) => setFormField("quantity", v)}
            error={errorDetails.quantity}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="brand_model"
            label="Brand / model"
            value={formData.brand_model}
            onChange={(v) => setFormField("brand_model", v)}
            error={errorDetails.brand_model}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="purchase_date"
            label="Purchase date"
            type="date"
            max={today}
            value={formData.purchase_date}
            onChange={(v) => setFormField("purchase_date", v)}
            error={errorDetails.purchase_date}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="purchase_value"
            label="Purchase value (₹)"
            type="text"
            inputMode="decimal"
            value={formData.purchase_value}
            onChange={(v) => setFormField("purchase_value", v)}
            error={errorDetails.purchase_value}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="supplier_vendor"
            label="Supplier / vendor"
            value={formData.supplier_vendor}
            onChange={(v) => setFormField("supplier_vendor", v)}
            error={errorDetails.supplier_vendor}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="invoice_bill_no"
            label="Invoice / bill no"
            value={formData.invoice_bill_no}
            maxLength={32}
            normalizeOnChangeValue={(v) =>
              String(v ?? "")
                .replace(/[^A-Za-z0-9]/g, "")
                .slice(0, 32)
            }
            onChange={(v) => setFormField("invoice_bill_no", v)}
            error={errorDetails.invoice_bill_no}
            baseAlertId={ALERT_ID}
            required
          />
        </>
      ) : null}

      <GlobalTextField
        id="location_room"
        label="Location / room"
        value={formData.location_room}
        onChange={(v) => setFormField("location_room", v)}
        error={errorDetails.location_room}
        baseAlertId={ALERT_ID}
        required
      />

      {isSubAdminLimitedEdit ? (
        <>
          <GlobalSelectField
            id="condition_id"
            label="Condition"
            value={formData.condition_id}
            onChange={(v) => setFormField("condition_id", v)}
            options={conditionOptions}
            error={errorDetails.condition_id}
            baseAlertId={ALERT_ID}
            required
            placeholder="Select condition"
          />
          <GlobalTextField
            id="brand_model"
            label="Brand / model"
            value={formData.brand_model}
            onChange={(v) => setFormField("brand_model", v)}
            error={errorDetails.brand_model}
            baseAlertId={ALERT_ID}
            required
          />
        </>
      ) : null}

      {showFullAssetForm ? (
        <>
          <GlobalSelectField
            id="condition_id"
            label="Condition"
            value={formData.condition_id}
            onChange={(v) => setFormField("condition_id", v)}
            options={conditionOptions}
            error={errorDetails.condition_id}
            baseAlertId={ALERT_ID}
            required
            placeholder="Select condition"
          />
          <GlobalSelectField
            id="property_status_id"
            label="Asset status"
            value={formData.property_status_id}
            onChange={(v) => setFormField("property_status_id", v)}
            options={statusOptions}
            error={errorDetails.property_status_id}
            baseAlertId={ALERT_ID}
            required
            placeholder="Select status"
          />
          <ResponsiblePersonSearch
            label="Responsible person"
            required
            value={formData.responsible_person_id}
            selectedUserLabel={responsibleLabel}
            onChange={(v) => setFormField("responsible_person_id", v)}
            onUserSelect={(u) => setResponsibleLabel(u.name || u.full_name || "")}
            error={errorDetails.responsible_person_id}
            baseAlertId={ALERT_ID}
          />
          <p className="gff-field gff-field--full">
            <label
              className={`gff-label${errorDetails.asset_image_url ? " gff-label--error" : ""}`}
              htmlFor="asset_image_url"
            >
              Asset image *
            </label>
            <div
              className={`gff-input${errorDetails.asset_image_url ? " gff-input--error" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.35rem 0.5rem",
              }}
            >
              <input
                id="asset_image_url"
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/jpeg,image/png"
                onChange={handleImageChange}
                aria-invalid={errorDetails.asset_image_url ? "true" : "false"}
                aria-describedby={
                  errorDetails.asset_image_url ? "asset-image-url-error" : undefined
                }
              />
              <GlobalButton
                type="button"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </GlobalButton>
              <span className="gff-help">
                {imageFile ? (
                  <>New file selected: {imageFile.name}</>
                ) : existingImagePath ? (
                  <a
                    href={buildPropertyAssetImageUrl(existingImagePath)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#15803d" }}
                  >
                    {getPropertyAssetImageFileName(existingImagePath)}
                  </a>
                ) : (
                  "No file chosen"
                )}
              </span>
            </div>
            {errorDetails.asset_image_url ? (
              <span id="asset-image-url-error" className="gff-error" role="alert">
                {errorDetails.asset_image_url}
              </span>
            ) : null}
          </p>
          <GlobalTextField
            id="warranty_expiry_date"
            label="Warranty expiry date"
            type="date"
            value={formData.warranty_expiry_date}
            onChange={(v) => setFormField("warranty_expiry_date", v)}
            error={errorDetails.warranty_expiry_date}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id="physical_verification_date"
            label="Physical verification date"
            type="date"
            max={today}
            value={formData.physical_verification_date}
            onChange={(v) => setFormField("physical_verification_date", v)}
            error={errorDetails.physical_verification_date}
            baseAlertId={ALERT_ID}
            required
          />
        </>
      ) : null}

      <GlobalTextAreaField
        id="remarks"
        label="Remarks"
        value={formData.remarks}
        onChange={(v) => setFormField("remarks", v)}
        error={errorDetails.remarks}
        baseAlertId={ALERT_ID}
        required
      />
    </GlobalFormShell>
  );
};

export default PropertyAssetForm;
